import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { WastePercentLines } from "../../types/waste-percent-report.type";
import {
  LINES_PER_BLOCK,
  MonthDay,
  WASTE_PERCENT_LINES,
  WastePercentLineKey,
  formatLineValue,
  isPercentLine,
} from "./wastePercentReport";
import {
  dayColumnSx,
  lineColumnSx,
  productColumnSx,
  sundayCellSx,
  totalColumnSx,
} from "./tableLayout";

const PRODUCT_ROW_SX = { bgcolor: "background.paper" } as const;
const TOTAL_ROW_SX = { bgcolor: "grey.100" } as const;
const PERCENT_ROW_SX = { "& td": { fontWeight: 600 } } as const;
const SUNDAY_DAY_COLUMN_SX = { ...dayColumnSx, ...sundayCellSx } as const;
const PRODUCT_NAME_WEIGHT = 500;
const TOTAL_NAME_WEIGHT = 700;

interface WastePercentLineBlockProps {
  /** A product's name, or a total block's. */
  title: string;
  /** A product's code, or the note under a total block's name. */
  subtitle?: string;
  lines: WastePercentLines;
  days: MonthDay[];
  /** A total block is tinted and its name set in bold, apart from the products above it. */
  isTotal: boolean;
}

/**
 * One product, or one total block, as the Excel sheet lays it out: its name spanning the eight
 * lines, then each line across the days of the month and its รวม.
 */
export default function WastePercentLineBlock({
  title,
  subtitle,
  lines,
  days,
  isTotal,
}: Readonly<WastePercentLineBlockProps>) {
  const rowSx = isTotal ? TOTAL_ROW_SX : PRODUCT_ROW_SX;
  const percentRowSx = [rowSx, PERCENT_ROW_SX];
  const titleWeight = isTotal ? TOTAL_NAME_WEIGHT : PRODUCT_NAME_WEIGHT;

  return (
    <>
      {WASTE_PERCENT_LINES.map(({ key, label }, lineIndex) => (
        <TableRow key={key} sx={isPercentLine(key) ? percentRowSx : rowSx}>
          {lineIndex === 0 && (
            <TableCell rowSpan={LINES_PER_BLOCK} sx={productColumnSx}>
              <Typography variant="body2" fontWeight={titleWeight}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </TableCell>
          )}
          <TableCell sx={lineColumnSx}>{label}</TableCell>
          <LineValueCells lineKey={key} lines={lines} days={days} />
        </TableRow>
      ))}
    </>
  );
}

interface LineValueCellsProps {
  lineKey: WastePercentLineKey;
  lines: WastePercentLines;
  days: MonthDay[];
}

function LineValueCells({ lineKey, lines, days }: Readonly<LineValueCellsProps>) {
  const line = lines[lineKey];

  return (
    <>
      {days.map((day, dayIndex) => (
        <TableCell
          key={day.dayNumber}
          align="right"
          sx={day.isSunday ? SUNDAY_DAY_COLUMN_SX : dayColumnSx}
        >
          {formatLineValue(lineKey, line.days[dayIndex])}
        </TableCell>
      ))}
      <TableCell align="right" sx={totalColumnSx}>
        {formatLineValue(lineKey, line.total)}
      </TableCell>
    </>
  );
}
